import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Supported languages
export const SUPPORTED_LANGUAGES = {
  it: { name: 'Italiano', flag: '🇮🇹' },
  en: { name: 'English', flag: '🇺🇸' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' }
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Translation interface
interface Translations {
  [key: string]: {
    [K in SupportedLanguage]: string;
  };
}

// Common translations
const translations: Translations = {
  // Navigation
  'nav.home': {
    it: 'Home',
    en: 'Home',
    es: 'Inicio',
    fr: 'Accueil',
    de: 'Startseite'
  },
  'nav.feed': {
    it: 'Feed',
    en: 'Feed',
    es: 'Feed',
    fr: 'Flux',
    de: 'Feed'
  },
  'nav.map': {
    it: 'Mappa',
    en: 'Map',
    es: 'Mapa',
    fr: 'Carte',
    de: 'Karte'
  },
  'nav.messages': {
    it: 'Chat',
    en: 'Messages',
    es: 'Mensajes',
    fr: 'Messages',
    de: 'Nachrichten'
  },
  'nav.profile': {
    it: 'Profilo',
    en: 'Profile',
    es: 'Perfil',
    fr: 'Profil',
    de: 'Profil'
  },
  
  // Common buttons
  'button.save': {
    it: 'Salva',
    en: 'Save',
    es: 'Guardar',
    fr: 'Enregistrer',
    de: 'Speichern'
  },
  'button.cancel': {
    it: 'Annulla',
    en: 'Cancel',
    es: 'Cancelar',
    fr: 'Annuler',
    de: 'Abbrechen'
  },
  'button.delete': {
    it: 'Elimina',
    en: 'Delete',
    es: 'Eliminar',
    fr: 'Supprimer',
    de: 'Löschen'
  },
  'button.edit': {
    it: 'Modifica',
    en: 'Edit',
    es: 'Editar',
    fr: 'Modifier',
    de: 'Bearbeiten'
  },
  'button.close': {
    it: 'Chiudi',
    en: 'Close',
    es: 'Cerrar',
    fr: 'Fermer',
    de: 'Schließen'
  },
  'button.send': {
    it: 'Invia',
    en: 'Send',
    es: 'Enviar',
    fr: 'Envoyer',
    de: 'Senden'
  },
  
  // Profile & Pets
  'profile.pets': {
    it: 'I Miei Pet',
    en: 'My Pets',
    es: 'Mis Mascotas',
    fr: 'Mes Animaux',
    de: 'Meine Haustiere'
  },
  'profile.posts': {
    it: 'Post',
    en: 'Posts',
    es: 'Publicaciones',
    fr: 'Publications',
    de: 'Beiträge'
  },
  'profile.followers': {
    it: 'Follower',
    en: 'Followers',
    es: 'Seguidores',
    fr: 'Abonnés',
    de: 'Follower'
  },
  'profile.following': {
    it: 'Seguiti',
    en: 'Following',
    es: 'Siguiendo',
    fr: 'Abonnements',
    de: 'Folge ich'
  },
  'profile.addPet': {
    it: 'Aggiungi Pet',
    en: 'Add Pet',
    es: 'Añadir Mascota',
    fr: 'Ajouter Animal',
    de: 'Haustier Hinzufügen'
  },
  'profile.createPost': {
    it: 'Crea Post',
    en: 'Create Post',
    es: 'Crear Publicación',
    fr: 'Créer Publication',
    de: 'Beitrag Erstellen'
  },
  
  // Adoption
  'adoption.saved': {
    it: 'Adozioni Salvate',
    en: 'Saved Adoptions',
    es: 'Adopciones Guardadas',
    fr: 'Adoptions Sauvegardées',
    de: 'Gespeicherte Adoptionen'
  },
  'adoption.requests': {
    it: 'Gestisci Richieste',
    en: 'Manage Requests',
    es: 'Gestionar Solicitudes',
    fr: 'Gérer les Demandes',
    de: 'Anfragen Verwalten'
  },
  'adoption.create': {
    it: 'Crea Annuncio',
    en: 'Create Ad',
    es: 'Crear Anuncio',
    fr: 'Créer Annonce',
    de: 'Anzeige Erstellen'
  },
  'adoption.title': {
    it: 'Pet in Adozione',
    en: 'Pets for Adoption',
    es: 'Mascotas en Adopción',
    fr: 'Animaux à Adopter',
    de: 'Haustiere zur Adoption'
  },
  
  // Menu items
  'menu.navigation': {
    it: 'Navigazione',
    en: 'Navigation',
    es: 'Navegación',
    fr: 'Navigation',
    de: 'Navigation'
  },
  'menu.community': {
    it: 'Community',
    en: 'Community',
    es: 'Comunidad',
    fr: 'Communauté',
    de: 'Gemeinschaft'
  },
  'menu.explore': {
    it: 'Esplora',
    en: 'Explore',
    es: 'Explorar',
    fr: 'Explorer',
    de: 'Erkunden'
  },
  'menu.myPets': {
    it: 'I miei Pet',
    en: 'My Pets',
    es: 'Mis Mascotas',
    fr: 'Mes Animaux',
    de: 'Meine Haustiere'
  },
  'menu.adoptions': {
    it: 'Adozioni',
    en: 'Adoptions',
    es: 'Adopciones',
    fr: 'Adoptions',
    de: 'Adoptionen'
  },
  'menu.receivedRequests': {
    it: 'Richieste Ricevute',
    en: 'Received Requests',
    es: 'Solicitudes Recibidas',
    fr: 'Demandes Reçues',
    de: 'Erhaltene Anfragen'
  },
  'menu.sentRequests': {
    it: 'Richieste Inviate',
    en: 'Sent Requests',
    es: 'Solicitudes Enviadas',
    fr: 'Demandes Envoyées',
    de: 'Gesendete Anfragen'
  },
  
  // Common terms
  'common.explore': {
    it: 'Esplora',
    en: 'Explore',
    es: 'Explorar',
    fr: 'Explorer',
    de: 'Erkunden'
  },
  'common.events': {
    it: 'Eventi',
    en: 'Events',
    es: 'Eventos',
    fr: 'Événements',
    de: 'Veranstaltungen'
  },
  'common.sos': {
    it: 'SOS',
    en: 'SOS',
    es: 'SOS',
    fr: 'SOS',
    de: 'SOS'
  },
  'common.notifications': {
    it: 'Notifiche',
    en: 'Notifications',
    es: 'Notificaciones',
    fr: 'Notifications',
    de: 'Benachrichtigungen'
  },
  'common.settings': {
    it: 'Impostazioni',
    en: 'Settings',
    es: 'Configuración',
    fr: 'Paramètres',
    de: 'Einstellungen'
  },
  
  // Page titles
  'page.home': {
    it: 'Home',
    en: 'Home',
    es: 'Inicio',
    fr: 'Accueil',
    de: 'Startseite'
  },
  'page.feed': {
    it: 'Feed',
    en: 'Feed',
    es: 'Feed',
    fr: 'Flux',
    de: 'Feed'
  },
  'page.map': {
    it: 'Mappa Community',
    en: 'Community Map',
    es: 'Mapa Comunidad',
    fr: 'Carte Communauté',
    de: 'Community-Karte'
  },
  'page.explore': {
    it: 'Esplora',
    en: 'Explore',
    es: 'Explorar',
    fr: 'Explorer',
    de: 'Erkunden'
  },
  
  // Feed page
  'feed.title': {
    it: 'Feed Personalizzato',
    en: 'Personalized Feed',
    es: 'Feed Personalizado',
    fr: 'Flux Personnalisé',
    de: 'Personalisierter Feed'
  },
  'feed.search': {
    it: 'Cerca utenti, hashtag o contenuti...',
    en: 'Search users, hashtags or content...',
    es: 'Buscar usuarios, hashtags o contenido...',
    fr: 'Rechercher utilisateurs, hashtags ou contenu...',
    de: 'Nutzer, Hashtags oder Inhalte suchen...'
  },
  'feed.suggested': {
    it: 'Utenti Suggeriti',
    en: 'Suggested Users',
    es: 'Usuarios Sugeridos',
    fr: 'Utilisateurs Suggérés',
    de: 'Vorgeschlagene Nutzer'
  },
  'feed.discover': {
    it: 'Scopri cosa succede nella community',
    en: 'Discover what\'s happening in the community',
    es: 'Descubre qué está pasando en la comunidad',
    fr: 'Découvrez ce qui se passe dans la communauté',
    de: 'Entdecke was in der Community passiert'
  },
  'feed.shareWithPets': {
    it: 'Condividi un momento con i tuoi pet...',
    en: 'Share a moment with your pets...',
    es: 'Comparte un momento con tus mascotas...',
    fr: 'Partagez un moment avec vos animaux...',
    de: 'Teile einen Moment mit deinen Haustieren...'
  },
  'feed.noPosts': {
    it: 'Nessun post da mostrare',
    en: 'No posts to show',
    es: 'No hay publicaciones que mostrar',
    fr: 'Aucune publication à afficher',
    de: 'Keine Beiträge zu zeigen'
  },
  'feed.followUsers': {
    it: 'Segui altri utenti per vedere i loro contenuti nel tuo feed personalizzato.',
    en: 'Follow other users to see their content in your personalized feed.',
    es: 'Sigue a otros usuarios para ver su contenido en tu feed personalizado.',
    fr: 'Suivez d\'autres utilisateurs pour voir leur contenu dans votre flux personnalisé.',
    de: 'Folge anderen Nutzern, um ihre Inhalte in deinem personalisierten Feed zu sehen.'
  },
  'feed.findUsers': {
    it: 'Trova Utenti da Seguire',
    en: 'Find Users to Follow',
    es: 'Encuentra Usuarios para Seguir',
    fr: 'Trouver des Utilisateurs à Suivre',
    de: 'Nutzer zum Folgen finden'
  },
  'feed.with': {
    it: 'con',
    en: 'with',
    es: 'con',
    fr: 'avec',
    de: 'mit'
  },
  'feed.reportPost': {
    it: 'Segnala post',
    en: 'Report post',
    es: 'Reportar publicación',
    fr: 'Signaler le post',
    de: 'Beitrag melden'
  },
  'feed.shareToChat': {
    it: 'Condividi in chat',
    en: 'Share to chat',
    es: 'Compartir en chat',
    fr: 'Partager en chat',
    de: 'In Chat teilen'
  },
  'feed.copyLink': {
    it: 'Copia link',
    en: 'Copy link',
    es: 'Copiar enlace',
    fr: 'Copier le lien',
    de: 'Link kopieren'
  },
  'feed.sharedToChat': {
    it: 'Post condiviso in chat!',
    en: 'Post shared to chat!',
    es: '¡Publicación compartida en chat!',
    fr: 'Post partagé en chat!',
    de: 'Beitrag in Chat geteilt!'
  },
  'feed.shareError': {
    it: 'Errore nel condividere il post',
    en: 'Error sharing the post',
    es: 'Error al compartir la publicación',
    fr: 'Erreur lors du partage du post',
    de: 'Fehler beim Teilen des Beitrags'
  },
  'feed.noChats': {
    it: 'Nessuna chat disponibile',
    en: 'No chats available',
    es: 'No hay chats disponibles',
    fr: 'Aucun chat disponible',
    de: 'Keine Chats verfügbar'
  },
  'feed.startConversation': {
    it: 'Inizia una conversazione per condividere post',
    en: 'Start a conversation to share posts',
    es: 'Inicia una conversación para compartir publicaciones',
    fr: 'Commencez une conversation pour partager des posts',
    de: 'Starte eine Unterhaltung um Beiträge zu teilen'
  },
  'feed.groupChat': {
    it: 'Chat di gruppo',
    en: 'Group chat',
    es: 'Chat grupal',
    fr: 'Chat de groupe',
    de: 'Gruppenchat'
  },
  'feed.directChat': {
    it: 'Chat diretta',
    en: 'Direct chat',
    es: 'Chat directo',
    fr: 'Chat direct',
    de: 'Direkter Chat'
  },
  'feed.group': {
    it: 'Gruppo',
    en: 'Group',
    es: 'Grupo',
    fr: 'Groupe',
    de: 'Gruppe'
  },
  
  // Map page
  'map.title': {
    it: 'Mappa Community',
    en: 'Community Map',
    es: 'Mapa Comunidad',
    fr: 'Carte Communauté',
    de: 'Community-Karte'
  },
  'map.description': {
    it: 'Scopri luoghi pet-friendly, veterinari, parchi e molto altro nella tua zona',
    en: 'Discover pet-friendly places, vets, parks and much more in your area',
    es: 'Descubre lugares pet-friendly, veterinarios, parques y mucho más en tu zona',
    fr: 'Découvrez des lieux pet-friendly, vétérinaires, parcs et bien plus dans votre région',
    de: 'Entdecke haustierfreundliche Orte, Tierärzte, Parks und vieles mehr in deiner Nähe'
  },
  'map.sosStats': {
    it: 'Statistiche SOS',
    en: 'SOS Statistics',
    es: 'Estadísticas SOS',
    fr: 'Statistiques SOS',
    de: 'SOS-Statistiken'
  },
  'map.totalReports': {
    it: 'Segnalazioni Totali',
    en: 'Total Reports',
    es: 'Reportes Totales',
    fr: 'Rapports Totaux',
    de: 'Gesamte Meldungen'
  },
  'map.openCases': {
    it: 'SOS Attivi',
    en: 'Active SOS',
    es: 'SOS Activos',
    fr: 'SOS Actifs',
    de: 'Aktive SOS'
  },
  'map.helpers': {
    it: 'Volontari Attivi',
    en: 'Active Volunteers',
    es: 'Voluntarios Activos',
    fr: 'Bénévoles Actifs',
    de: 'Aktive Helfer'
  },
  'map.locations': {
    it: 'Luoghi Pet-Friendly',
    en: 'Pet-Friendly Places',
    es: 'Lugares Pet-Friendly',
    fr: 'Lieux Pet-Friendly',
    de: 'Haustierfreundliche Orte'
  },
  'map.reportedPlaces': {
    it: 'Luoghi Segnalati',
    en: 'Reported Places',
    es: 'Lugares Reportados',
    fr: 'Lieux Signalés',
    de: 'Gemeldete Orte'
  },
  'map.helpingPeople': {
    it: 'Persone che Aiutano',
    en: 'People Helping',
    es: 'Personas Ayudando',
    fr: 'Personnes qui Aident',
    de: 'Helfende Personen'
  },
  'map.helpedPets': {
    it: 'Pet Aiutati',
    en: 'Pets Helped',
    es: 'Mascotas Ayudadas',
    fr: 'Animaux Aidés',
    de: 'Geholfen Haustiere'
  },
  'map.howToUse': {
    it: 'Come utilizzare la mappa',
    en: 'How to use the map',
    es: 'Cómo usar el mapa',
    fr: 'Comment utiliser la carte',
    de: 'Wie man die Karte benutzt'
  },
  'map.navigation': {
    it: 'Navigazione',
    en: 'Navigation',
    es: 'Navegación',
    fr: 'Navigation',
    de: 'Navigation'
  },
  'map.addPoints': {
    it: 'Aggiungere Punti',
    en: 'Adding Points',
    es: 'Agregar Puntos',
    fr: 'Ajouter des Points',
    de: 'Punkte Hinzufügen'
  },
  'map.categories': {
    it: 'Categorie',
    en: 'Categories',
    es: 'Categorías',
    fr: 'Catégories',
    de: 'Kategorien'
  },
  'map.emergencies': {
    it: 'Emergenze',
    en: 'Emergencies',
    es: 'Emergencias',
    fr: 'Urgences',
    de: 'Notfälle'
  },
  
  // Search
  'search.placeholder': {
    it: 'Cerca pet, persone, luoghi...',
    en: 'Search pets, people, places...',
    es: 'Buscar mascotas, personas, lugares...',
    fr: 'Rechercher animaux, personnes, lieux...',
    de: 'Suche Haustiere, Personen, Orte...'
  },
  'search.mobile': {
    it: 'Cerca...',
    en: 'Search...',
    es: 'Buscar...',
    fr: 'Rechercher...',
    de: 'Suchen...'
  },
  
  // Language selector
  'language.select': {
    it: 'Seleziona Lingua',
    en: 'Select Language',
    es: 'Seleccionar Idioma',
    fr: 'Sélectionner Langue',
    de: 'Sprache Auswählen'
  },
  
  // Time
  'time.ago': {
    it: 'fa',
    en: 'ago',
    es: 'hace',
    fr: 'il y a',
    de: 'vor'
  },
  'time.now': {
    it: 'adesso',
    en: 'now',
    es: 'ahora',
    fr: 'maintenant',
    de: 'jetzt'
  },
  
  // Content
  'content.noResults': {
    it: 'Nessun risultato trovato',
    en: 'No results found',
    es: 'No se encontraron resultados',
    fr: 'Aucun résultat trouvé',
    de: 'Keine Ergebnisse gefunden'
  },
  'content.loading': {
    it: 'Caricamento...',
    en: 'Loading...',
    es: 'Cargando...',
    fr: 'Chargement...',
    de: 'Laden...'
  },
  
  // Welcome & Auth
  'welcome.title': {
    it: 'Benvenuto in AstroPet',
    en: 'Welcome to AstroPet',
    es: 'Bienvenido a AstroPet',
    fr: 'Bienvenue sur AstroPet',
    de: 'Willkommen bei AstroPet'
  },
  'auth.login': {
    it: 'Accedi',
    en: 'Sign In',
    es: 'Iniciar Sesión',
    fr: 'Se Connecter',
    de: 'Anmelden'
  },
  'auth.register': {
    it: 'Registrati',
    en: 'Sign Up',
    es: 'Registrarse',
    fr: 'S\'inscrire',
    de: 'Registrieren'
  },
  
  // Home page specific
  'home.welcome': {
    it: 'Ciao, {name}! 🚀',
    en: 'Hello, {name}! 🚀',
    es: '¡Hola, {name}! 🚀',
    fr: 'Bonjour, {name}! 🚀',
    de: 'Hallo, {name}! 🚀'
  },
  'home.subtitle': {
    it: 'Cosa vuoi fare oggi con i tuoi pet?',
    en: 'What do you want to do today with your pets?',
    es: '¿Qué quieres hacer hoy con tus mascotas?',
    fr: 'Que voulez-vous faire aujourd\'hui avec vos animaux?',
    de: 'Was möchtest du heute mit deinen Haustieren machen?'
  },
  'home.quickActions': {
    it: 'Azioni Rapide',
    en: 'Quick Actions',
    es: 'Acciones Rápidas',
    fr: 'Actions Rapides',
    de: 'Schnellaktionen'
  },
  'home.createPost': {
    it: 'Crea Post',
    en: 'Create Post',
    es: 'Crear Publicación',
    fr: 'Créer Publication',
    de: 'Beitrag Erstellen'
  },
  'home.trendingToday': {
    it: 'Trending Oggi',
    en: 'Trending Today',
    es: 'Tendencias Hoy',
    fr: 'Tendances Aujourd\'hui',
    de: 'Trending Heute'
  },
  'home.personalizedFeed': {
    it: 'Feed Personalizzato',
    en: 'Personalized Feed',
    es: 'Feed Personalizado',
    fr: 'Flux Personnalisé',
    de: 'Personalisierter Feed'
  },
  'home.feedDescription': {
    it: 'I post più recenti dalla tua community',
    en: 'The latest posts from your community',
    es: 'Las publicaciones más recientes de tu comunidad',
    fr: 'Les dernières publications de votre communauté',
    de: 'Die neuesten Beiträge aus deiner Community'
  },
  'home.seeAllPosts': {
    it: 'Vedi tutti i post',
    en: 'See all posts',
    es: 'Ver todas las publicaciones',
    fr: 'Voir tous les posts',
    de: 'Alle Beiträge ansehen'
  },
  'home.startFollowing': {
    it: 'Inizia a seguire pet e proprietari!',
    en: 'Start following pets and owners!',
    es: '¡Comienza a seguir mascotas y propietarios!',
    fr: 'Commencez à suivre des animaux et propriétaires!',
    de: 'Beginne Haustiere und Besitzer zu folgen!'
  },
  'home.feedWillAppear': {
    it: 'Il tuo feed personalizzato apparirà qui quando inizierai a seguire altri utenti.',
    en: 'Your personalized feed will appear here when you start following other users.',
    es: 'Tu feed personalizado aparecerá aquí cuando comiences a seguir a otros usuarios.',
    fr: 'Votre flux personnalisé apparaîtra ici lorsque vous commencerez à suivre d\'autres utilisateurs.',
    de: 'Dein personalisierter Feed wird hier erscheinen, wenn du anfängst, anderen Nutzern zu folgen.'
  },
  'home.exploreCommunity': {
    it: 'Esplora Community',
    en: 'Explore Community',
    es: 'Explorar Comunidad',
    fr: 'Explorer Communauté',
    de: 'Community Erkunden'
  },
  'home.yourPets': {
    it: 'I Tuoi Pet',
    en: 'Your Pets',
    es: 'Tus Mascotas',
    fr: 'Vos Animaux',
    de: 'Deine Haustiere'
  },
  'home.seeAll': {
    it: 'Vedi tutti ({count})',
    en: 'See all ({count})',
    es: 'Ver todos ({count})',
    fr: 'Voir tout ({count})',
    de: 'Alle ansehen ({count})'
  },
  'home.noPetsYet': {
    it: 'Non hai ancora aggiunto nessun pet',
    en: 'You haven\'t added any pets yet',
    es: 'Aún no has añadido ninguna mascota',
    fr: 'Vous n\'avez pas encore ajouté d\'animaux',
    de: 'Du hast noch keine Haustiere hinzugefügt'
  },
  'home.addPet': {
    it: 'Aggiungi Pet',
    en: 'Add Pet',
    es: 'Añadir Mascota',
    fr: 'Ajouter Animal',
    de: 'Haustier Hinzufügen'
  },
  'home.user': {
    it: 'Utente',
    en: 'User',
    es: 'Usuario',
    fr: 'Utilisateur',
    de: 'Benutzer'
  },

  // Adoption translations
  'adoption.petForAdoption': {
    it: 'Pet in adozione',
    en: 'Pet for adoption',
    es: 'Mascota en adopción',
    fr: 'Animal à adopter',
    de: 'Haustier zur Adoption'
  },
  'adoption.yourLocation': {
    it: 'La tua posizione',
    en: 'Your location',
    es: 'Tu ubicación',
    fr: 'Votre position',
    de: 'Deine Position'
  },
  'adoption.legend': {
    it: 'Legenda',
    en: 'Legend',
    es: 'Leyenda',
    fr: 'Légende',
    de: 'Legende'
  },
  'adoption.details': {
    it: 'Dettagli Adozione',
    en: 'Adoption Details',
    es: 'Detalles de Adopción',
    fr: 'Détails Adoption',
    de: 'Adoptions-Details'
  },
  'adoption.description': {
    it: 'Descrizione',
    en: 'Description',
    es: 'Descripción',
    fr: 'Description',
    de: 'Beschreibung'
  },
  'adoption.characteristics': {
    it: 'Caratteristiche',
    en: 'Characteristics',
    es: 'Características',
    fr: 'Caractéristiques',
    de: 'Eigenschaften'
  },
  'adoption.requestTitle': {
    it: 'Richiesta di Adozione',
    en: 'Adoption Request',
    es: 'Solicitud de Adopción',
    fr: 'Demande d\'Adoption',
    de: 'Adoptionsanfrage'
  },
  'adoption.messagePlaceholder': {
    it: 'Presentati al proprietario e spiega perché vorresti adottare questo pet...',
    en: 'Introduce yourself to the owner and explain why you would like to adopt this pet...',
    es: 'Preséntate al propietario y explica por qué te gustaría adoptar esta mascota...',
    fr: 'Présentez-vous au propriétaire et expliquez pourquoi vous souhaitez adopter cet animal...',
    de: 'Stell dich dem Besitzer vor und erkläre, warum du dieses Haustier adoptieren möchtest...'
  },
  'adoption.availabilityPlaceholder': {
    it: 'es. Disponibile nei weekend, dopo le 18...',
    en: 'e.g. Available on weekends, after 6 PM...',
    es: 'ej. Disponible los fines de semana, después de las 18h...',
    fr: 'ex. Disponible le week-end, après 18h...',
    de: 'z.B. Verfügbar am Wochenende, nach 18 Uhr...'
  },
  'adoption.guidelines': {
    it: 'Linee Guida',
    en: 'Guidelines',
    es: 'Pautas',
    fr: 'Directives',
    de: 'Richtlinien'
  },
  'adoption.allSpecies': {
    it: 'Tutte le specie',
    en: 'All species',
    es: 'Todas las especies',
    fr: 'Toutes les espèces',
    de: 'Alle Arten'
  },
  'adoption.dogs': {
    it: 'Cani',
    en: 'Dogs',
    es: 'Perros',
    fr: 'Chiens',
    de: 'Hunde'
  },
  'adoption.cats': {
    it: 'Gatti',
    en: 'Cats',
    es: 'Gatos',
    fr: 'Chats',
    de: 'Katzen'
  },
  'adoption.rabbits': {
    it: 'Conigli',
    en: 'Rabbits',
    es: 'Conejos',
    fr: 'Lapins',
    de: 'Kaninchen'
  },
  'adoption.others': {
    it: 'Altri',
    en: 'Others',
    es: 'Otros',
    fr: 'Autres',
    de: 'Andere'
  },
  'adoption.requestsTitle': {
    it: 'Richieste di Adozione',
    en: 'Adoption Requests',
    es: 'Solicitudes de Adopción',
    fr: 'Demandes d\'Adoption',
    de: 'Adoptionsanfragen'
  },
  'adoption.requestDetails': {
    it: 'Dettagli Richiesta',
    en: 'Request Details',
    es: 'Detalles de la Solicitud',
    fr: 'Détails de la Demande',
    de: 'Anfragedetails'
  },
  'adoption.bio': {
    it: 'Bio',
    en: 'Bio',
    es: 'Biografía',
    fr: 'Bio',
    de: 'Bio'
  },
  'adoption.requestedPet': {
    it: 'Pet richiesto',
    en: 'Requested pet',
    es: 'Mascota solicitada',
    fr: 'Animal demandé',
    de: 'Angefragtes Haustier'
  },
  'adoption.message': {
    it: 'Messaggio',
    en: 'Message',
    es: 'Mensaje',
    fr: 'Message',
    de: 'Nachricht'
  },
  'adoption.replyToRequest': {
    it: 'Rispondi alla Richiesta',
    en: 'Reply to Request',
    es: 'Responder a la Solicitud',
    fr: 'Répondre à la Demande',
    de: 'Auf Anfrage antworten'
  },

  // Auth translations
  'auth.welcomeToFuture': {
    it: 'Benvenuto nel futuro dei pet',
    en: 'Welcome to the future of pets',
    es: 'Bienvenido al futuro de las mascotas',
    fr: 'Bienvenue dans l\'avenir des animaux',
    de: 'Willkommen in der Zukunft der Haustiere'
  },
  'auth.enterOTP': {
    it: 'Inserisci il codice OTP',
    en: 'Enter OTP code',
    es: 'Introduce el código OTP',
    fr: 'Entrez le code OTP',
    de: 'OTP-Code eingeben'
  },
  'auth.email': {
    it: 'Email',
    en: 'Email',
    es: 'Correo electrónico',
    fr: 'Email',
    de: 'E-Mail'
  },
  'auth.password': {
    it: 'Password',
    en: 'Password',
    es: 'Contraseña',
    fr: 'Mot de passe',
    de: 'Passwort'
  },
  'auth.username': {
    it: 'Username',
    en: 'Username',
    es: 'Nombre de usuario',
    fr: 'Nom d\'utilisateur',
    de: 'Benutzername'
  },
  'auth.resetPassword': {
    it: 'Reset Password',
    en: 'Reset Password',
    es: 'Restablecer Contraseña',
    fr: 'Réinitialiser Mot de Passe',
    de: 'Passwort Zurücksetzen'
  },

  // Events translations
  'events.createNew': {
    it: 'Crea Nuovo Evento',
    en: 'Create New Event',
    es: 'Crear Nuevo Evento',
    fr: 'Créer Nouvel Événement',
    de: 'Neues Event Erstellen'
  },
  'events.editEvent': {
    it: 'Modifica Evento',
    en: 'Edit Event',
    es: 'Editar Evento',
    fr: 'Modifier Événement',
    de: 'Event Bearbeiten'
  },
  'events.namePlaceholder': {
    it: 'Es. Passeggiata al parco con cani',
    en: 'E.g. Walk in the park with dogs',
    es: 'Ej. Paseo en el parque con perros',
    fr: 'Ex. Promenade au parc avec chiens',
    de: 'z.B. Spaziergang im Park mit Hunden'
  },
  'events.descriptionPlaceholder': {
    it: 'Descrivi il tuo evento...',
    en: 'Describe your event...',
    es: 'Describe tu evento...',
    fr: 'Décrivez votre événement...',
    de: 'Beschreibe dein Event...'
  },
  'events.maxParticipants': {
    it: 'Max partecipanti',
    en: 'Max participants',
    es: 'Máx participantes',
    fr: 'Max participants',
    de: 'Max Teilnehmer'
  },
  'events.unlimited': {
    it: 'Illimitato',
    en: 'Unlimited',
    es: 'Ilimitado',
    fr: 'Illimité',
    de: 'Unbegrenzt'
  },
  'events.registrationDeadline': {
    it: 'Scadenza iscrizioni',
    en: 'Registration deadline',
    es: 'Fecha límite de inscripción',
    fr: 'Date limite d\'inscription',
    de: 'Anmeldeschluss'
  },
  'events.setLocation': {
    it: 'Imposta posizione',
    en: 'Set location',
    es: 'Establecer ubicación',
    fr: 'Définir l\'emplacement',
    de: 'Standort festlegen'
  },
  'events.updateLocation': {
    it: 'Aggiorna posizione',
    en: 'Update location',
    es: 'Actualizar ubicación',
    fr: 'Mettre à jour l\'emplacement',
    de: 'Standort aktualisieren'
  },
  'events.searchAddress': {
    it: 'Cerca indirizzo...',
    en: 'Search address...',
    es: 'Buscar dirección...',
    fr: 'Rechercher adresse...',
    de: 'Adresse suchen...'
  },
  'events.shareEvent': {
    it: 'Condividi evento',
    en: 'Share event',
    es: 'Compartir evento',
    fr: 'Partager événement',
    de: 'Event teilen'
  },
  'events.openInMaps': {
    it: 'Apri in Google Maps',
    en: 'Open in Google Maps',
    es: 'Abrir en Google Maps',
    fr: 'Ouvrir dans Google Maps',
    de: 'In Google Maps öffnen'
  },
  'events.deleteEvent': {
    it: 'Elimina Evento',
    en: 'Delete Event',
    es: 'Eliminar Evento',
    fr: 'Supprimer Événement',
    de: 'Event Löschen'
  },
  'events.noEventsScheduled': {
    it: 'Nessun evento in programma',
    en: 'No events scheduled',
    es: 'No hay eventos programados',
    fr: 'Aucun événement programmé',
    de: 'Keine Events geplant'
  },

  // Profile & Pet translations
  'profile.createPet': {
    it: 'Aggiungi Pet',
    en: 'Add Pet',
    es: 'Añadir Mascota',
    fr: 'Ajouter Animal',
    de: 'Haustier Hinzufügen'
  },
  'profile.petNamePlaceholder': {
    it: 'Es. Luna',
    en: 'E.g. Luna',
    es: 'Ej. Luna',
    fr: 'Ex. Luna',
    de: 'z.B. Luna'
  },
  'profile.selectSpecies': {
    it: 'Seleziona',
    en: 'Select',
    es: 'Seleccionar',
    fr: 'Sélectionner',
    de: 'Auswählen'
  },
  'profile.breedPlaceholder': {
    it: 'Es. Golden Retriever',
    en: 'E.g. Golden Retriever',
    es: 'Ej. Golden Retriever',
    fr: 'Ex. Golden Retriever',
    de: 'z.B. Golden Retriever'
  },
  'profile.ageYears': {
    it: 'Anni',
    en: 'Years',
    es: 'Años',
    fr: 'Ans',
    de: 'Jahre'
  },
  'profile.petDescription': {
    it: 'Racconta qualcosa di speciale sul tuo pet...',
    en: 'Tell something special about your pet...',
    es: 'Cuenta algo especial sobre tu mascota...',
    fr: 'Racontez quelque chose de spécial sur votre animal...',
    de: 'Erzähle etwas Besonderes über dein Haustier...'
  },
  'profile.postPlaceholder': {
    it: 'Cosa vuoi condividere oggi?',
    en: 'What do you want to share today?',
    es: '¿Qué quieres compartir hoy?',
    fr: 'Que voulez-vous partager aujourd\'hui?',
    de: 'Was möchtest du heute teilen?'
  },
  'profile.selectPet': {
    it: 'Seleziona un pet',
    en: 'Select a pet',
    es: 'Seleccionar una mascota',
    fr: 'Sélectionner un animal',
    de: 'Haustier auswählen'
  },
  'profile.addLocation': {
    it: 'Aggiungi una posizione...',
    en: 'Add a location...',
    es: 'Añadir una ubicación...',
    fr: 'Ajouter un emplacement...',
    de: 'Standort hinzufügen...'
  },

  // Map translations
  'map.addPoint': {
    it: 'Aggiungi Punto',
    en: 'Add Point',
    es: 'Añadir Punto',
    fr: 'Ajouter Point',
    de: 'Punkt Hinzufügen'
  },
  'map.placeName': {
    it: 'Nome del luogo...',
    en: 'Place name...',
    es: 'Nombre del lugar...',
    fr: 'Nom du lieu...',
    de: 'Name des Ortes...'
  },
  'map.category': {
    it: 'Categoria',
    en: 'Category',
    es: 'Categoría',
    fr: 'Catégorie',
    de: 'Kategorie'
  },
  'map.selectCategory': {
    it: 'Seleziona una categoria',
    en: 'Select a category',
    es: 'Seleccionar una categoría',
    fr: 'Sélectionner une catégorie',
    de: 'Kategorie auswählen'
  },
  'map.address': {
    it: 'Indirizzo',
    en: 'Address',
    es: 'Dirección',
    fr: 'Adresse',
    de: 'Adresse'
  },
  'map.addressPlaceholder': {
    it: 'Via, numero civico, città...',
    en: 'Street, number, city...',
    es: 'Calle, número, ciudad...',
    fr: 'Rue, numéro, ville...',
    de: 'Straße, Nummer, Stadt...'
  },
  'map.descriptionPlaceholder': {
    it: 'Descrivi il luogo, orari, servizi...',
    en: 'Describe the place, hours, services...',
    es: 'Describe el lugar, horarios, servicios...',
    fr: 'Décrivez le lieu, horaires, services...',
    de: 'Beschreibe den Ort, Öffnungszeiten, Services...'
  },
  'map.clickToAdd': {
    it: 'Clicca sulla mappa per aggiungere un punto',
    en: 'Click on the map to add a point',
    es: 'Haz clic en el mapa para añadir un punto',
    fr: 'Cliquez sur la carte pour ajouter un point',
    de: 'Klicke auf die Karte um einen Punkt hinzuzufügen'
  },
  'map.veterinarian': {
    it: 'Veterinario',
    en: 'Veterinarian',
    es: 'Veterinario',
    fr: 'Vétérinaire',
    de: 'Tierarzt'
  },
  'map.park': {
    it: 'Parco',
    en: 'Park',
    es: 'Parque',
    fr: 'Parc',
    de: 'Park'
  },
  'map.shops': {
    it: 'Negozi',
    en: 'Shops',
    es: 'Tiendas',
    fr: 'Magasins',
    de: 'Geschäfte'
  },
  'map.adoption': {
    it: 'Adozione',
    en: 'Adoption',
    es: 'Adopción',
    fr: 'Adoption',
    de: 'Adoption'
  },
  'map.lost': {
    it: 'Smarriti',
    en: 'Lost',
    es: 'Perdidos',
    fr: 'Perdus',
    de: 'Verloren'
  },

  // Messages translations
  'messages.newChat': {
    it: 'Nuova Chat',
    en: 'New Chat',
    es: 'Nuevo Chat',
    fr: 'Nouveau Chat',
    de: 'Neuer Chat'
  },
  'messages.searchUsers': {
    it: 'Cerca utenti...',
    en: 'Search users...',
    es: 'Buscar usuarios...',
    fr: 'Rechercher utilisateurs...',
    de: 'Nutzer suchen...'
  },
  'messages.searchChats': {
    it: 'Cerca chat...',
    en: 'Search chats...',
    es: 'Buscar chats...',
    fr: 'Rechercher chats...',
    de: 'Chats suchen...'
  },
  'messages.startWriting': {
    it: 'Inizia a scrivere',
    en: 'Start writing',
    es: 'Comienza a escribir',
    fr: 'Commencer à écrire',
    de: 'Beginne zu schreiben'
  },
  'messages.typeMessage': {
    it: 'Scrivi un messaggio...',
    en: 'Type a message...',
    es: 'Escribe un mensaje...',
    fr: 'Tapez un message...',
    de: 'Nachricht eingeben...'
  },
  'messages.online': {
    it: 'Online',
    en: 'Online',
    es: 'En línea',
    fr: 'En ligne',
    de: 'Online'
  },

  // Account deletion
  'account.deleted': {
    it: 'Account Eliminato',
    en: 'Account Deleted',
    es: 'Cuenta Eliminada',
    fr: 'Compte Supprimé',
    de: 'Konto Gelöscht'
  },
  'account.deletionCompleted': {
    it: 'Eliminazione Completata',
    en: 'Deletion Completed',
    es: 'Eliminación Completada',
    fr: 'Suppression Terminée',
    de: 'Löschung Abgeschlossen'
  },
  'account.confirmDelete': {
    it: 'Scrivi ELIMINA per confermare',
    en: 'Type DELETE to confirm',
    es: 'Escribe ELIMINAR para confirmar',
    fr: 'Tapez SUPPRIMER pour confirmer',
    de: 'Schreibe LÖSCHEN zur Bestätigung'
  },

  // General UI
  'ui.logout': {
    it: 'Esci',
    en: 'Logout',
    es: 'Cerrar Sesión',
    fr: 'Déconnexion',
    de: 'Abmelden'
  },
  'ui.profile': {
    it: 'Profilo',
    en: 'Profile',
    es: 'Perfil',
    fr: 'Profil',
    de: 'Profil'
  },
  'ui.madeWith': {
    it: 'Fatto con',
    en: 'Made with',
    es: 'Hecho con',
    fr: 'Fait avec',
    de: 'Gemacht mit'
  },
  'ui.forAnimalLovers': {
    it: 'per gli amanti degli animali',
    en: 'for animal lovers',
    es: 'para los amantes de los animales',
    fr: 'pour les amoureux des animaux',
    de: 'für Tierliebhaber'
  },
  'ui.noResultsFound': {
    it: 'Nessun risultato trovato',
    en: 'No results found',
    es: 'No se encontraron resultados',
    fr: 'Aucun résultat trouvé',
    de: 'Keine Ergebnisse gefunden'
  },
  'ui.suggestedUsers': {
    it: 'Utenti Suggeriti',
    en: 'Suggested Users',
    es: 'Usuarios Sugeridos',
    fr: 'Utilisateurs Suggérés',
    de: 'Vorgeschlagene Nutzer'
  },
  'ui.legalInfo': {
    it: 'Informazioni Legali',
    en: 'Legal Information',
    es: 'Información Legal',
    fr: 'Informations Légales',
    de: 'Rechtliche Informationen'
  },
  'ui.support': {
    it: 'Supporto',
    en: 'Support',
    es: 'Soporte',
    fr: 'Support',
    de: 'Support'
  },

  // Cookie Policy
  'cookies.policy': {
    it: 'Cookie Policy',
    en: 'Cookie Policy',
    es: 'Política de Cookies',
    fr: 'Politique de Cookies',
    de: 'Cookie-Richtlinie'
  },
  'cookies.preferences': {
    it: 'Preferenze Cookie',
    en: 'Cookie Preferences',
    es: 'Preferencias de Cookies',
    fr: 'Préférences Cookies',
    de: 'Cookie-Einstellungen'
  },
  'cookies.necessary': {
    it: 'Cookie Necessari',
    en: 'Necessary Cookies',
    es: 'Cookies Necesarias',
    fr: 'Cookies Nécessaires',
    de: 'Notwendige Cookies'
  },
  'cookies.performance': {
    it: 'Cookie di Performance',
    en: 'Performance Cookies',
    es: 'Cookies de Rendimiento',
    fr: 'Cookies de Performance',
    de: 'Performance-Cookies'
  },
  'cookies.marketing': {
    it: 'Cookie di Marketing',
    en: 'Marketing Cookies',
    es: 'Cookies de Marketing',
    fr: 'Cookies Marketing',
    de: 'Marketing-Cookies'
  },

  // Additional UI actions and labels
  'action.save': {
    it: 'Salva',
    en: 'Save',
    es: 'Guardar',
    fr: 'Sauvegarder',
    de: 'Speichern'
  },
  'action.saving': {
    it: 'Salvando...',
    en: 'Saving...',
    es: 'Guardando...',
    fr: 'Sauvegarde...',
    de: 'Speichern...'
  },
  'action.create': {
    it: 'Crea',
    en: 'Create',
    es: 'Crear',
    fr: 'Créer',
    de: 'Erstellen'
  },
  'action.creating': {
    it: 'Creazione...',
    en: 'Creating...',
    es: 'Creando...',
    fr: 'Création...',
    de: 'Erstellen...'
  },
  'action.update': {
    it: 'Aggiorna',
    en: 'Update',
    es: 'Actualizar',
    fr: 'Mettre à jour',
    de: 'Aktualisieren'
  },
  'action.share': {
    it: 'Condividi',
    en: 'Share',
    es: 'Compartir',
    fr: 'Partager',
    de: 'Teilen'
  },
  'action.upload': {
    it: 'Carica',
    en: 'Upload',
    es: 'Subir',
    fr: 'Télécharger',
    de: 'Hochladen'
  },
  'action.download': {
    it: 'Scarica',
    en: 'Download',
    es: 'Descargar',
    fr: 'Télécharger',
    de: 'Herunterladen'
  },
  'action.search': {
    it: 'Cerca',
    en: 'Search',
    es: 'Buscar',
    fr: 'Rechercher',
    de: 'Suchen'
  },
  'action.filter': {
    it: 'Filtra',
    en: 'Filter',
    es: 'Filtrar',
    fr: 'Filtrer',
    de: 'Filtern'
  },
  'action.remove': {
    it: 'Rimuovi',
    en: 'Remove',
    es: 'Eliminar',
    fr: 'Supprimer',
    de: 'Entfernen'
  },
  'action.add': {
    it: 'Aggiungi',
    en: 'Add',
    es: 'Añadir',
    fr: 'Ajouter',
    de: 'Hinzufügen'
  },
  'action.view': {
    it: 'Visualizza',
    en: 'View',
    es: 'Ver',
    fr: 'Voir',
    de: 'Ansehen'
  },
  'action.back': {
    it: 'Indietro',
    en: 'Back',
    es: 'Atrás',
    fr: 'Retour',
    de: 'Zurück'
  },
  'action.next': {
    it: 'Avanti',
    en: 'Next',
    es: 'Siguiente',
    fr: 'Suivant',
    de: 'Weiter'
  },
  'action.continue': {
    it: 'Continua',
    en: 'Continue',
    es: 'Continuar',
    fr: 'Continuer',
    de: 'Weiter'
  },
  'action.confirm': {
    it: 'Conferma',
    en: 'Confirm',
    es: 'Confirmar',
    fr: 'Confirmer',
    de: 'Bestätigen'
  },
  'action.submit': {
    it: 'Invia',
    en: 'Submit',
    es: 'Enviar',
    fr: 'Envoyer',
    de: 'Senden'
  },
  'action.sending': {
    it: 'Invio...',
    en: 'Sending...',
    es: 'Enviando...',
    fr: 'Envoi...',
    de: 'Senden...'
  },
  'action.saved': {
    it: 'Salvato',
    en: 'Saved',
    es: 'Guardado',
    fr: 'Sauvegardé',
    de: 'Gespeichert'
  },
  'action.published': {
    it: 'Pubblicato',
    en: 'Published',
    es: 'Publicado',
    fr: 'Publié',
    de: 'Veröffentlicht'
  },

  // Status messages
  'status.loading': {
    it: 'Caricamento...',
    en: 'Loading...',
    es: 'Cargando...',
    fr: 'Chargement...',
    de: 'Laden...'
  },
  'status.success': {
    it: 'Successo',
    en: 'Success',
    es: 'Éxito',
    fr: 'Succès',
    de: 'Erfolg'
  },
  'status.error': {
    it: 'Errore',
    en: 'Error',
    es: 'Error',
    fr: 'Erreur',
    de: 'Fehler'
  },
  'status.completed': {
    it: 'Completato',
    en: 'Completed',
    es: 'Completado',
    fr: 'Terminé',
    de: 'Abgeschlossen'
  },
  'status.pending': {
    it: 'In attesa',
    en: 'Pending',
    es: 'Pendiente',
    fr: 'En attente',
    de: 'Ausstehend'
  },

  // More specific translations
  'cookie.savePreferences': {
    it: 'Salva Preferenze',
    en: 'Save Preferences',
    es: 'Guardar Preferencias',
    fr: 'Sauvegarder Préférences',
    de: 'Einstellungen Speichern'
  },
  'location.yourPosition': {
    it: 'La tua posizione',
    en: 'Your position',
    es: 'Tu posición',
    fr: 'Votre position',
    de: 'Deine Position'
  },
  'location.accuracy': {
    it: 'Precisione',
    en: 'Accuracy',
    es: 'Precisión',
    fr: 'Précision',
    de: 'Genauigkeit'
  },
  'location.updated': {
    it: 'Posizione aggiornata',
    en: 'Position updated',
    es: 'Posición actualizada',
    fr: 'Position mise à jour',
    de: 'Position aktualisiert'
  },
  'location.searchAddress': {
    it: 'Cerca indirizzo',
    en: 'Search address',
    es: 'Buscar dirección',
    fr: 'Rechercher adresse',
    de: 'Adresse suchen'
  },

  // Event specific
  'events.saveChanges': {
    it: 'Salva Modifiche',
    en: 'Save Changes',
    es: 'Guardar Cambios',
    fr: 'Sauvegarder Modifications',
    de: 'Änderungen Speichern'
  },
  'events.cancelRegistration': {
    it: 'Annulla Iscrizione',
    en: 'Cancel Registration',
    es: 'Cancelar Inscripción',
    fr: 'Annuler Inscription',
    de: 'Anmeldung Stornieren'
  },
  'events.eventCreated': {
    it: 'Evento creato con successo!',
    en: 'Event created successfully!',
    es: '¡Evento creado con éxito!',
    fr: 'Événement créé avec succès!',
    de: 'Event erfolgreich erstellt!'
  },
  'events.eventUpdated': {
    it: 'Evento aggiornato con successo!',
    en: 'Event updated successfully!',
    es: '¡Evento actualizado con éxito!',
    fr: 'Événement mis à jour avec succès!',
    de: 'Event erfolgreich aktualisiert!'
  },
  'events.errorCreating': {
    it: 'Errore nella creazione evento',
    en: 'Error creating event',
    es: 'Error al crear evento',
    fr: 'Erreur lors de la création événement',
    de: 'Fehler beim Erstellen des Events'
  },
  'events.errorUpdating': {
    it: "Errore nell'aggiornamento evento",
    en: 'Error updating event',
    es: 'Error al actualizar evento',
    fr: 'Erreur lors de la mise à jour événement',
    de: 'Fehler beim Aktualisieren des Events'
  },

  // Adoption specific  
  'adoption.alreadyRequested': {
    it: 'Richiesta già inviata',
    en: 'Request already sent',
    es: 'Solicitud ya enviada',
    fr: 'Demande déjà envoyée',
    de: 'Anfrage bereits gesendet'
  },
  'adoption.alreadyRequestedDesc': {
    it: 'Hai già inviato una richiesta per questo pet',
    en: 'You have already sent a request for this pet',
    es: 'Ya has enviado una solicitud para esta mascota',
    fr: 'Vous avez déjà envoyé une demande pour cet animal',
    de: 'Du hast bereits eine Anfrage für dieses Haustier gesendet'
  },
  'adoption.requestSent': {
    it: 'Richiesta inviata!',
    en: 'Request sent!',
    es: '¡Solicitud enviada!',
    fr: 'Demande envoyée!',
    de: 'Anfrage gesendet!'
  },
  'adoption.errorSending': {
    it: 'Impossibile inviare la richiesta. Riprova più tardi.',
    en: 'Unable to send request. Please try again later.',
    es: 'No se puede enviar la solicitud. Inténtalo de nuevo más tarde.',
    fr: 'Impossible d\'envoyer la demande. Veuillez réessayer plus tard.',
    de: 'Anfrage kann nicht gesendet werden. Bitte versuche es später erneut.'
  },
  'adoption.sendRequest': {
    it: 'Invia Richiesta',
    en: 'Send Request',
    es: 'Enviar Solicitud',
    fr: 'Envoyer Demande',
    de: 'Anfrage Senden'
  },
  'adoption.loginToSave': {
    it: "Effettua l'accesso per salvare gli annunci di adozione",
    en: 'Log in to save adoption ads',
    es: 'Inicia sesión para guardar anuncios de adopción',
    fr: 'Connectez-vous pour sauvegarder les annonces d\'adoption',
    de: 'Melde dich an, um Adoptionsanzeigen zu speichern'
  },
  'adoption.removedFromSaved': {
    it: 'Rimosso dai salvati',
    en: 'Removed from saved',
    es: 'Eliminado de guardados',
    fr: 'Supprimé des favoris',
    de: 'Aus Gespeicherten entfernt'
  },
  'adoption.removedFromSavedDesc': {
    it: "L'annuncio di adozione è stato rimosso dai tuoi salvati",
    en: 'The adoption ad has been removed from your saved items',
    es: 'El anuncio de adopción ha sido eliminado de tus guardados',
    fr: "L'annonce d'adoption a été supprimée de vos favoris",
    de: 'Die Adoptionsanzeige wurde aus deinen Gespeicherten entfernt'
  },
  'adoption.adSaved': {
    it: 'Salvato!',
    en: 'Saved!',
    es: '¡Guardado!',
    fr: 'Sauvegardé!',
    de: 'Gespeichert!'
  },
  'adoption.adSavedDesc': {
    it: "L'annuncio di adozione è stato aggiunto ai tuoi salvati",
    en: 'The adoption ad has been added to your saved items',
    es: 'El anuncio de adopción ha sido añadido a tus guardados',
    fr: "L'annonce d'adoption a été ajoutée à vos favoris",
    de: 'Die Adoptionsanzeige wurde zu deinen Gespeicherten hinzugefügt'
  },
  'adoption.errorSaving': {
    it: "Impossibile salvare l'annuncio di adozione",
    en: 'Unable to save the adoption ad',
    es: 'No se puede guardar el anuncio de adopción',
    fr: "Impossible de sauvegarder l'annonce d'adoption",
    de: 'Adoptionsanzeige kann nicht gespeichert werden'
  },
  'adoption.closeDetails': {
    it: 'Chiudi Dettagli',
    en: 'Close Details',
    es: 'Cerrar Detalles',
    fr: 'Fermer Détails',
    de: 'Details Schließen'
  },

  // Search and results
  'search.errorSearch': {
    it: 'Errore durante la ricerca',
    en: 'Error during search',
    es: 'Error durante la búsqueda',
    fr: 'Erreur lors de la recherche',
    de: 'Fehler bei der Suche'
  },
  'search.searchHashtag': {
    it: 'Cerca questo hashtag',
    en: 'Search this hashtag',
    es: 'Buscar este hashtag',
    fr: 'Rechercher ce hashtag',
    de: 'Diesen Hashtag suchen'
  },

  // Shop translations
  'shop.title': {
    it: 'Shop Community',
    en: 'Community Shop',
    es: 'Tienda Comunitaria',
    fr: 'Boutique Communautaire',
    de: 'Community-Shop'
  },
  'shop.connectYourStore': {
    it: 'Collega il tuo Negozio',
    en: 'Connect Your Store',
    es: 'Conecta tu Tienda',
    fr: 'Connecter votre Boutique',
    de: 'Deinen Shop Verbinden'
  },
  'shop.myShop': {
    it: 'Il mio Shop',
    en: 'My Shop',
    es: 'Mi Tienda',
    fr: 'Ma Boutique',
    de: 'Mein Shop'
  },
  'shop.browseShops': {
    it: 'Esplora Negozi',
    en: 'Browse Shops',
    es: 'Explorar Tiendas',
    fr: 'Parcourir Boutiques',
    de: 'Shops Durchsuchen'
  },
  'shop.shopName': {
    it: 'Nome del Negozio',
    en: 'Shop Name',
    es: 'Nombre de la Tienda',
    fr: 'Nom de la Boutique',
    de: 'Shop-Name'
  },
  'shop.shopUrl': {
    it: 'URL del Negozio',
    en: 'Shop URL',
    es: 'URL de la Tienda',
    fr: 'URL de la Boutique',
    de: 'Shop-URL'
  },
  'shop.shopDescription': {
    it: 'Descrizione del Negozio',
    en: 'Shop Description',
    es: 'Descripción de la Tienda',
    fr: 'Description de la Boutique',
    de: 'Shop-Beschreibung'
  },
  'shop.shopifyDomain': {
    it: 'Dominio Shopify (opzionale)',
    en: 'Shopify Domain (optional)',
    es: 'Dominio Shopify (opcional)',
    fr: 'Domaine Shopify (optionnel)',
    de: 'Shopify-Domain (optional)'
  },
  'shop.shopifyAccessToken': {
    it: 'Access Token Shopify',
    en: 'Shopify Access Token',
    es: 'Token de Acceso Shopify',
    fr: 'Token d\'Accès Shopify',
    de: 'Shopify-Zugriffstoken'
  },
  'shop.logoUrl': {
    it: 'URL Logo (opzionale)',
    en: 'Logo URL (optional)',
    es: 'URL del Logo (opcional)',
    fr: 'URL du Logo (optionnel)',
    de: 'Logo-URL (optional)'
  },
  'shop.makePublic': {
    it: 'Rendi pubblico',
    en: 'Make public',
    es: 'Hacer público',
    fr: 'Rendre public',
    de: 'Öffentlich machen'
  },
  'shop.isActive': {
    it: 'Negozio attivo',
    en: 'Shop active',
    es: 'Tienda activa',
    fr: 'Boutique active',
    de: 'Shop aktiv'
  },
  'shop.connected': {
    it: 'Negozio collegato con successo!',
    en: 'Shop connected successfully!',
    es: '¡Tienda conectada exitosamente!',
    fr: 'Boutique connectée avec succès!',
    de: 'Shop erfolgreich verbunden!'
  },
  'shop.errorConnecting': {
    it: 'Errore nel collegare il negozio',
    en: 'Error connecting shop',
    es: 'Error al conectar la tienda',
    fr: 'Erreur lors de la connexion boutique',
    de: 'Fehler beim Verbinden des Shops'
  },
  'shop.visitShop': {
    it: 'Visita Negozio',
    en: 'Visit Shop',
    es: 'Visitar Tienda',
    fr: 'Visiter Boutique',
    de: 'Shop Besuchen'
  },
  'shop.noShops': {
    it: 'Nessun negozio disponibile',
    en: 'No shops available',
    es: 'No hay tiendas disponibles',
    fr: 'Aucune boutique disponible',
    de: 'Keine Shops verfügbar'
  },
  'shop.connectFirst': {
    it: 'Collega il tuo primo negozio per iniziare a vendere nella community!',
    en: 'Connect your first shop to start selling in the community!',
    es: '¡Conecta tu primera tienda para empezar a vender en la comunidad!',
    fr: 'Connectez votre première boutique pour commencer à vendre dans la communauté!',
    de: 'Verbinde deinen ersten Shop, um in der Community zu verkaufen!'
  },
  'shop.updated': {
    it: 'Negozio aggiornato con successo!',
    en: 'Shop updated successfully!',
    es: '¡Tienda actualizada exitosamente!',
    fr: 'Boutique mise à jour avec succès!',
    de: 'Shop erfolgreich aktualisiert!'
  },
  'shop.errorUpdating': {
    it: 'Errore nell\'aggiornare il negozio',
    en: 'Error updating shop',
    es: 'Error al actualizar la tienda',
    fr: 'Erreur lors de la mise à jour boutique',
    de: 'Fehler beim Aktualisieren des Shops'
  }
};

interface TranslationContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  t: (key: string, fallback?: string) => string;
  isLoading: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('it');
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Load user language preference
  useEffect(() => {
    const loadLanguagePreference = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_language_preferences')
          .select('language_code')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading language preference:', error);
        } else if (data?.language_code) {
          setCurrentLanguage(data.language_code as SupportedLanguage);
        }
      } catch (error) {
        console.error('Error loading language preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguagePreference();
  }, [user]);

  // Set language function
  const setLanguage = async (language: SupportedLanguage) => {
    setCurrentLanguage(language);

    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_language_preferences')
        .upsert({
          user_id: user.id,
          language_code: language
        });

      if (error) {
        console.error('Error saving language preference:', error);
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  // Translation function
  const t = (key: string, fallback?: string): string => {
    const translation = translations[key];
    if (!translation) {
      return fallback || key;
    }
    return translation[currentLanguage] || translation.it || fallback || key;
  };

  const value: TranslationContextType = {
    currentLanguage,
    setLanguage,
    t,
    isLoading
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};